<div
  class="overflow-hidden rounded-lg border border-base-content/20"
>
  <!-- Sort controls (replaces table header) -->
  <div
    class="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-base-300/40 bg-base-300/10 px-4 py-3 text-sm"
  >
    <span class="font-medium text-base-content/70">Sort by</span>
    <button
      type="button"
      class="link-hover link inline-flex items-center gap-1 font-medium"
      (click)="sortChange.emit('default')"
    >
      Default
    </button>
    <button
      type="button"
      class="link-hover link inline-flex items-center gap-1 font-medium"
      (click)="sortChange.emit('product.code')"
    >
      Product
      @if (currentSort?.field === 'product.code') {
        @if (currentSort?.direction === 'ASC') {
          <app-chevron-up-icon class="inline-block h-4 w-4" />
        } @else {
          <app-chevron-down-icon class="inline-block h-4 w-4" />
        }
      }
    </button>
    <button
      type="button"
      class="link-hover link inline-flex items-center gap-1 font-medium"
      (click)="sortChange.emit('unique_id')"
    >
      Unique ID
      @if (currentSort?.field === 'unique_id') {
        @if (currentSort?.direction === 'ASC') {
          <app-chevron-up-icon class="inline-block h-4 w-4" />
        } @else {
          <app-chevron-down-icon class="inline-block h-4 w-4" />
        }
      }
    </button>
    <button
      type="button"
      class="link-hover link inline-flex items-center gap-1 font-medium"
      (click)="sortChange.emit('expiration')"
    >
      Expiration
      @if (currentSort?.field === 'expiration') {
        @if (currentSort?.direction === 'ASC') {
          <app-chevron-up-icon class="inline-block h-4 w-4" />
        } @else {
          <app-chevron-down-icon class="inline-block h-4 w-4" />
        }
      }
    </button>
    <button
      type="button"
      class="link-hover link inline-flex items-center gap-1 font-medium"
      (click)="sortChange.emit('created_at')"
    >
      Created
      @if (currentSort?.field === 'created_at') {
        @if (currentSort?.direction === 'ASC') {
          <app-chevron-up-icon class="inline-block h-4 w-4" />
        } @else {
          <app-chevron-down-icon class="inline-block h-4 w-4" />
        }
      }
    </button>
  </div>

  <div class="divide-y divide-base-300">
    @for (license of licenses ?? []; track license.id) {
      <article
        class="license-row px-4 py-4"
        [ngClass]="{ 'bg-base-200': $index % 2 === 0 }"
      >
        <div
          class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between"
        >
          <div
            class="grid min-w-0 flex-1 grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6"
          >
            <div class="col-span-2 min-w-0 lg:col-span-4 xl:col-span-2">
              <div class="field-label">Product</div>
              <div class="break-words text-base font-medium">
                {{ license.product.code }} - {{ license.product.title }} ({{
                  license.product.variance_code
                }})
              </div>
            </div>

            <div class="min-w-0">
              <div class="field-label">Type</div>
              <div class="break-words text-base">
                {{ getLicenseType(license) }}
              </div>
            </div>

            <div class="min-w-0">
              <div class="field-label">Duration</div>
              <div class="break-words text-base">
                {{ license.product_variant.display_duration }}
              </div>
            </div>

            <div class="min-w-0">
              <div class="field-label">Price</div>
              <div class="break-words text-base">
                {{ getLicensePrice(license) | currency }}
              </div>
            </div>

            <div class="min-w-0">
              <div class="field-label">Expiration</div>
              <div class="break-words text-base">
                {{ license.expiration | date: 'MM/dd/yyyy' : 'UTC' }}
              </div>
            </div>

            <div class="col-span-2 min-w-0">
              <div class="field-label">
                Activation Key
                @if (licenseCurrentUniqueId(license)) {
                  <app-refresh-icon
                    (click)="showRegenerateKeyDialog(license)"
                    class="mb-[-4px] ml-2 inline-block h-4 w-4 cursor-pointer"
                    title="Regenerate activation key"
                  />
                }
                @if (license.activation_key) {
                  @if (shownKeys.has(license.id)) {
                    <app-eye-slash-icon
                      (click)="shownKeys.delete(license.id)"
                      class="mb-[-4px] ml-2 inline-block h-4 w-4 cursor-pointer"
                      title="Hide activation key"
                    />
                  } @else {
                    <app-eye-icon
                      (click)="shownKeys.add(license.id)"
                      class="mb-[-4px] ml-2 inline-block h-4 w-4 cursor-pointer"
                      title="Show activation key"
                    />
                  }
                  <app-file-copy-icon
                    (click)="copyActivationKey(license, $event)"
                    class="mb-[-4px] ml-2 inline-block h-4 w-4 cursor-pointer"
                    title="Copy activation key"
                  />
                }
              </div>
              <div class="break-words text-base">
                @if (license.activation_key) {
                  @if (shownKeys.has(license.id)) {
                    <span class="text-nowrap text-sm">{{
                      license.activation_key.activation_key
                    }}</span>
                  } @else {
                    <span [innerHTML]="hiddenKeyString"></span>
                  }
                } @else {
                  <span class="text-base italic">None</span>
                }
              </div>
            </div>

            <div class="min-w-0">
              <div class="field-label">Unique ID</div>
              <div class="break-words text-base">
                @if (license.unique_id || license.activation_key) {
                  {{
                    license.unique_id ||
                      license.activation_key?.unique_id ||
                      '—'
                  }}
                } @else {
                  <span class="text-base italic">None</span>
                }
              </div>
            </div>

            <div class="min-w-0">
              <div class="field-label">Transfers Left</div>
              <div class="break-words text-base">
                {{ license.transfers_left }}
              </div>
            </div>

            <div class="min-w-0">
              <div class="field-label">Created</div>
              <div class="break-words text-base">
                {{ license.created_at | date: 'MM/dd/yyyy, hh:mmaa' }}
              </div>
            </div>

            <div class="min-w-0">
              <div class="field-label">Invoice</div>
              <div class="break-words text-base">
                @if (latestInvoice(license)) {
                  @if (latestInvoice(license)!.completed) {
                    <a
                      class="link"
                      (click)="browseToInvoice(latestInvoice(license)!)"
                      >Paid</a
                    >
                  } @else {
                    <a
                      class="link"
                      (click)="browseToInvoice(latestInvoice(license)!)"
                      >Pending</a
                    >
                  }
                } @else {
                  N/A
                }
              </div>
            </div>
          </div>

          <div class="flex shrink-0 justify-end">
            <div class="flex flex-col space-y-1">
              <span>
                <a
                  [routerLink]="'/admin/edit-license/' + license.id"
                  class="inline-flex"
                  title="Edit License"
                >
                  <span
                    class="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-base-300/40 hover:text-base-content/80"
                  >
                    <app-edit-icon></app-edit-icon>
                  </span>
                </a>
              </span>
              <span>
                <span
                  class="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md hover:bg-base-300/40 hover:text-base-content/80"
                  (click)="showTransferDialog(license)"
                  title="Transfer License/Update Unique ID"
                >
                  <app-sort-horizontal-icon></app-sort-horizontal-icon>
                </span>
              </span>
              <span>
                <a
                  [routerLink]="'/admin/license-change-history/' + license.id"
                  class="inline-flex"
                  title="View change history"
                >
                  <span
                    class="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-base-300/40 hover:text-base-content/80"
                  >
                    <app-clock-icon></app-clock-icon>
                  </span>
                </a>
              </span>
            </div>
          </div>
        </div>

        @if (hasNotes(license)) {
          <div class="pt-4">
            <div class="field-label">Notes</div>
            <div class="mt-1 flex min-w-0 items-start gap-2">
              <div
                #noteRow
                [attr.data-license-id]="license.id"
                class="license-notes-text min-w-0 flex-1 text-sm text-base-content/70"
                [class.license-notes-clamped]="!isNoteExpanded(license.id)"
              >
                {{ license.notes }}
              </div>
              @if (showNoteToggle(license.id)) {
                <button
                  type="button"
                  class="btn btn-square btn-ghost btn-xs shrink-0"
                  (click)="toggleNoteExpand(license.id, $event)"
                  [attr.aria-expanded]="isNoteExpanded(license.id)"
                  [attr.aria-label]="
                    isNoteExpanded(license.id)
                      ? 'Collapse license notes'
                      : 'Expand license notes'
                  "
                >
                  @if (isNoteExpanded(license.id)) {
                    <app-chevron-up-icon class="h-4 w-4" />
                  } @else {
                    <app-chevron-down-icon class="h-4 w-4" />
                  }
                </button>
              }
            </div>
          </div>
        }
      </article>
    } @empty {
      <div class="bg-base-200/30 px-4 py-8 text-center text-base-content/70">
        No licenses
      </div>
    }
  </div>
</div>

<dialog #transferDialog class="modal">
  <div class="modal-box max-w-3xl">
    <form method="dialog">
      <button
        (click)="closeTransferDialog()"
        class="btn btn-circle btn-ghost btn-sm absolute right-2 top-2"
      >
        ✕
      </button>
    </form>

    <h3 class="text-lg font-bold">Transfer License</h3>

    <div class="mt-4">
      <span class="mr-2 text-base">Current Unique ID:</span>
      <span class="text-base">
        {{ licenseToTransfer?.activation_key?.unique_id || 'None' }}
      </span>
    </div>

    @if (sameUidLicensesList.length > 0) {
      <p class="mt-4 text-base font-medium">
        Transfer other licenses with same Unique ID?
      </p>
      <div class="mt-2 overflow-x-auto rounded-lg border border-base-content/20">
        <table class="table table-sm">
          <thead>
            <tr>
              <th></th>
              <th>Product</th>
              <th>Type</th>
              <th>Transfers left</th>
              <th>Expiration</th>
            </tr>
          </thead>
          <tbody>
            @for (license of sameUidLicensesList; track license.id) {
              <tr>
                <td>
                  <input
                    type="checkbox"
                    class="checkbox"
                    [checked]="sameUidSelected.has(license.id)"
                    (change)="
                      setSameUidSelected(
                        license.id,
                        $any($event.target).checked
                      )
                    "
                  />
                </td>
                <td class="max-w-xs break-words">
                  {{ license.product.code }} — {{ license.product.title }}
                </td>
                <td>{{ getLicenseType(license) }}</td>
                <td>{{ license.transfers_left }}</td>
                <td>
                  {{ license.expiration | date: 'MM/dd/yyyy' : 'UTC' }}
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }

    <hr class="my-4 border-base-content/50" />

    <p class="text-base italic">
      * This process will automatically generate a new activation key for this
      license.
    </p>

    <div class="form-control mt-2 w-fit">
      <label for="isTransfer" class="label cursor-pointer">
        <input
          type="checkbox"
          class="checkbox"
          id="isTransfer"
          [(ngModel)]="isTransfer"
        />
        <span class="label-text pl-2 text-base">This is a transfer</span>
      </label>
    </div>
    @if (isTransfer) {
      <div class="form-control mt-2 w-fit">
        <label class="label cursor-pointer">
          <input
            type="checkbox"
            class="checkbox"
            [(ngModel)]="syncRegistrationDatabase"
          />
          <span class="label-text pl-2 text-base"
            >Update registration database (deactivate old machine, activate
            new)</span
          >
        </label>
      </div>
    }
    <div class="form-control mt-2">
      <label class="label">
        <span class="label-text">Enter a new Unique ID</span>
      </label>
      <input
        #newUniqueIdInput
        type="text"
        class="input input-bordered"
        placeholder="New Unique ID"
        [(ngModel)]="newUniqueId"
      />
    </div>

    @if (loadingMachines) {
      <div class="skeleton mb-4 mt-8 h-12 w-full"></div>
    } @else if (errorLoadingMachines) {
      <div class="mt-2">
        <div class="text-base text-error">{{ errorLoadingMachines }}</div>
      </div>
    } @else {
      @if (distinctActiveMachinesList.length > 0) {
        <div class="mb-2 mt-4 flex w-full items-center justify-center gap-2">
          <div class="h-0 flex-grow border-b border-base-content/50"></div>
          <span class="flex-shr text-base-content/70">&nbsp;OR&nbsp;</span>
          <div class="h-0 flex-grow border-b border-base-content/50"></div>
        </div>

        <div class="form-control">
          <label class="label pt-0">
            <span class="label-text">Select existing machine</span>
          </label>
          <ul
            class="mt-2 max-h-60 divide-y divide-base-content/20 overflow-y-auto rounded-lg border border-base-content/20"
            role="radiogroup"
            aria-label="Active registrations by unique ID"
          >
            @for (
              machine of distinctActiveMachinesList;
              track machine.registration_id
            ) {
              <li>
                <label
                  class="flex cursor-pointer items-start gap-3 px-3 py-2.5 hover:bg-base-200"
                >
                  <input
                    type="radio"
                    name="transferRegistrationPick"
                    class="radio-primary radio mt-0.5 shrink-0"
                    [checked]="isRegistrationSelected(machine)"
                    (change)="selectRegistrationForTransfer(machine)"
                  />
                  <span class="min-w-0 flex-1 text-base">
                    <span class="font-medium">{{ machine.uniqueid }}</span>
                    @if (machine.computername) {
                      <span class="text-base-content/80">
                        — {{ machine.computername }}
                      </span>
                    }
                    @if (machine.lastused) {
                      <span class="mt-0.5 block text-sm text-base-content/70">
                        Last used:
                        {{ machine.lastused | date: 'MM/dd/yyyy, hh:mmaa' }}
                      </span>
                    }
                  </span>
                </label>
              </li>
            }
          </ul>
        </div>
      }
    }

    <div class="modal-action">
      @if (transferingLicense) {
        <div class="loading-spinner mr-4"></div>
      }
      <button
        (click)="transferLicense()"
        class="btn btn-primary"
        [disabled]="transferingLicense"
      >
        Transfer
      </button>
      <button
        class="btn btn-secondary"
        (click)="closeTransferDialog()"
        [disabled]="transferingLicense"
      >
        Cancel
      </button>
      @if (errorTransferingLicense) {
        <div class="mt-2 text-base text-error">
          {{ errorTransferingLicense }}
        </div>
      }
    </div>
  </div>
  <form method="dialog" class="modal-backdrop">
    <button>close</button>
  </form>
</dialog>

<dialog #regenerateKeyDialog class="modal">
  <div class="modal-box">
    <form method="dialog">
      <button
        (click)="closeRegenerateKeyDialog()"
        class="btn btn-circle btn-ghost btn-sm absolute right-2 top-2"
        [disabled]="regeneratingKey"
      >
        ✕
      </button>
    </form>

    <h3 class="text-lg font-bold">Regenerate Activation Key?</h3>
    <p class="py-4">
      This creates a new activation key for this license using its current
      Unique ID and license details. The old key will no longer be the latest.
    </p>
    <p class="text-sm">
      Unique ID:
      {{
        licenseToRegenerate
          ? licenseCurrentUniqueId(licenseToRegenerate) || 'None'
          : 'None'
      }}
    </p>
    @if (errorRegeneratingKey) {
      <p class="mt-2 text-error">{{ errorRegeneratingKey }}</p>
    }
    <div class="modal-action">
      <button
        class="btn"
        (click)="closeRegenerateKeyDialog()"
        [disabled]="regeneratingKey"
      >
        Cancel
      </button>
      <button
        class="btn btn-primary"
        (click)="confirmRegenerateKey()"
        [disabled]="
          regeneratingKey ||
          !licenseToRegenerate ||
          !licenseCurrentUniqueId(licenseToRegenerate)
        "
      >
        @if (regeneratingKey) {
          Generating…
        } @else {
          Generate new key
        }
      </button>
    </div>
  </div>
  <form method="dialog" class="modal-backdrop">
    <button>close</button>
  </form>
</dialog>
